import { useMemo, useState } from 'react';
import { Form, FormGroup, Grid, Panel, Select } from '@bigcommerce/big-design';
import { useClient } from '../../../context/client';
import { useAsyncEffect } from '../../../hooks';
import { useVehicleList } from '../../../hooks/useVehicleList';

function createOptions(options) {
	return options.map((value) => ({ value, content: value }));
}

function useVendorList() {
	const [vendors, setVendors] = useState([]);
	const client = useClient();

	useAsyncEffect(async () => {
		try {
			setVendors(
				(await client.getVendors())
					.filter((vendor) => vendor !== '')
					.sort(),
			);
		} catch (err) {
			console.error(err);
		}
	});

	return vendors;
}

function useVendorOption(vendors) {
	const [selectedVendor, setSelectedVendor] = useState('');
	const vendorOptions = useMemo(
		() => createOptions(['', ...vendors]),
		[vendors],
	);

	return [selectedVendor, setSelectedVendor, vendorOptions];
}

function useVehicleYearOption(vehiclesTree) {
	const [selectedYear, setSelectedYear] = useState('');
	const yearOptions = useMemo(
		() =>
			createOptions([
				'',
				// Sort by year descending
				...Object.keys(vehiclesTree).sort(
					(leftYear, rightYear) =>
						Number(rightYear) - Number(leftYear),
				),
			]),
		[vehiclesTree],
	);

	return [selectedYear, setSelectedYear, yearOptions];
}

function useVehicleMakeOption(vehiclesTree, selectedYear) {
	const [selectedMake, setSelectedMake] = useState('');
	const makeOptions = useMemo(
		() =>
			createOptions([
				'',
				...Object.keys(vehiclesTree?.[selectedYear] || {}).sort(),
			]),
		[selectedYear, vehiclesTree],
	);

	return [selectedMake, setSelectedMake, makeOptions];
}

function useVehicleModelOption(vehiclesTree, selectedYear, selectedMake) {
	const [selectedModel, setSelectedModel] = useState('');
	const modelOptions = useMemo(
		() =>
			createOptions([
				'',
				...Object.keys(
					vehiclesTree?.[selectedYear]?.[selectedMake] || {},
				).sort(),
			]),
		[selectedYear, vehiclesTree, selectedMake],
	);

	return [selectedModel, setSelectedModel, modelOptions];
}

const EXPORT_TYPES = {
	TEMPLATE: 'template',
	DATA: 'data',
};

export default function Export() {
	const [exporting, setExporting] = useState(false);
	const [exportType, setExportingType] = useState('template');
	const [, vehicles] = useVehicleList();
	const vendors = useVendorList();
	const client = useClient();

	const [selectedVendor, setSelectedVendor, vendorOptions] =
		useVendorOption(vendors);
	const [selectedYear, setSelectedYear, yearOptions] =
		useVehicleYearOption(vehicles);
	const [selectedMake, setSelectedMake, makeOptions] = useVehicleMakeOption(
		vehicles,
		selectedYear,
	);
	const [selectedModel, setSelectedModel, modelOptions] =
		useVehicleModelOption(vehicles, selectedYear, selectedMake);

	return (
		<Panel
			header="Export"
			action={{
				variant: 'primary',
				text: 'Download',
				isLoading: exporting,
				onClick: async () => {
					setExporting(true);
					await client.exportFitment(
						exportType === EXPORT_TYPES.TEMPLATE,
						{
							year: selectedYear,
							make: selectedMake,
							model: selectedModel,
							vendor: selectedVendor,
						},
					);
					setExporting(false);
				},
			}}
		>
			<Form>
				<FormGroup>
					<Select
						label="Export Type"
						onOptionChange={setExportingType}
						value={exportType}
						required
						options={[
							{
								value: EXPORT_TYPES.TEMPLATE,
								content: 'Template',
								description: 'Empty CSV file with columns.',
							},
							{
								value: EXPORT_TYPES.DATA,
								content: 'Data',
								description:
									'CSV file with filtered fit database data.',
							},
						]}
					/>
				</FormGroup>
				{exportType === 'data' && (
					<FormGroup>
						<Grid gridColumns="repeat(4, 1fr)">
							<Select
								label="Year"
								onOptionChange={setSelectedYear}
								value={selectedYear}
								options={yearOptions}
							/>
							<Select
								label="Make"
								onOptionChange={setSelectedMake}
								value={selectedMake}
								options={makeOptions}
								disabled={!selectedYear.length}
							/>
							<Select
								label="Model"
								onOptionChange={setSelectedModel}
								value={selectedModel}
								options={modelOptions}
								disabled={!selectedMake.length}
							/>
							<Select
								label="Vendor"
								onOptionChange={setSelectedVendor}
								value={selectedVendor}
								options={vendorOptions}
							/>
						</Grid>
					</FormGroup>
				)}
			</Form>
		</Panel>
	);
}
